/**
 * DesktopHero — RunAI WebOS Ana Ekran (Widget Tabanlı)
 * SWR + localStorage cache, skeleton loaders, React.memo optimization
 * Live Wallpaper + Workspace Snapshot
 */
import { Bot, CheckCircle2, FolderOpen, Image as ImageIcon, Save, X, Zap } from 'lucide-react'
import { memo, useEffect, useState } from 'react'
import { API_URL, apiFetch } from '../../../config/api'
import { useResponsive } from '../../../hooks/useResponsive'
import { DesktopContextMenu } from './DesktopContextMenu'
import { MetricsWidget, PulseWidget, TwinWidget } from './MetricsAndTwinWidgets'
import { useWidgetVisibility, type WidgetId } from './useWidgetVisibility'
import { WALLPAPER_PRESETS, type WallpaperId } from './wallpaperConfig'

// ── Types ─────────────────────────────────────────────────────────────────────
interface Task { id: number; title: string; status: string; category?: string }

// ── Live Wallpaper Effect ─────────────────────────────────────────────────────
function useLiveWallpaper() {
  useEffect(() => {
    const checkAgentActivity = async () => {
      try {
        const res = await apiFetch(`${API_URL}/api/flzhub/messages?limit=1`)
        const data = await res.json()
        const lastMsg = data.messages?.[0]
        if (!lastMsg?.created_at) return

        const msgTime = new Date(lastMsg.created_at).getTime()
        const now = Date.now()
        const diff = now - msgTime

        const osArea = document.querySelector('[data-area="os"]') as HTMLElement
        if (!osArea) return

        if (diff < 5 * 60 * 1000) {
          document.body.classList.add('agent-active')
        } else {
          document.body.classList.remove('agent-active')
        }
      } catch (err) {
        console.error('Live wallpaper check failed:', err)
      }
    }

    checkAgentActivity()
    const interval = setInterval(checkAgentActivity, 30000)
    return () => clearInterval(interval)
  }, [])
}

// ── Wallpaper Picker ──────────────────────────────────────────────────────────
function WallpaperPicker() {
  const [isOpen, setIsOpen] = useState(false)
  const handleSelect = (id: WallpaperId) => {
    localStorage.setItem('os-wallpaper', id)
    window.dispatchEvent(new CustomEvent('wallpaper-change', { detail: id }))
    setIsOpen(false)
  }
  return (
    <div style={{ position: 'absolute', bottom: 24, right: 24, zIndex: 100 }}>
      <button onClick={() => setIsOpen(!isOpen)} title="Change wallpaper" style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--os-surface)', backdropFilter: 'blur(20px)', border: '1px solid var(--os-border)', color: 'var(--os-text)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: 'var(--os-elevation-1)' }}>
        <ImageIcon size={16} />
      </button>
      {isOpen && (
        <div style={{ position: 'absolute', bottom: 40, right: 0, padding: 8, background: 'var(--os-surface)', backdropFilter: 'blur(20px)', border: '1px solid var(--os-border)', borderRadius: 12, display: 'grid', gridTemplateColumns: 'repeat(3, 40px)', gap: 8, boxShadow: 'var(--os-elevation-2)' }}>
          {(Object.keys(WALLPAPER_PRESETS) as WallpaperId[]).map(id => (
            <div key={id} onClick={() => handleSelect(id)} title={id} style={{ width: 40, height: 40, borderRadius: 6, cursor: 'pointer', ...WALLPAPER_PRESETS[id], border: '1px solid var(--os-border)' }} />
          ))}
        </div>
      )}
    </div>
  )
}

// ── Widget container (macOS Sonoma sağ panel için) ─────────────────────────
function Widget({ id, children, style, onClick }: { id?: WidgetId, children: React.ReactNode, style?: React.CSSProperties, onClick?: () => void }) {
  const { hide } = useWidgetVisibility()
  const [hover, setHover] = useState(false)

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={onClick}
      style={{
        width: '100%',
        minHeight: 100,
        position: 'relative',
        background: 'var(--os-card-2)',
        backdropFilter: 'blur(24px)',
        WebkitBackdropFilter: 'blur(24px)',
        border: '1px solid var(--os-card-border)',
        borderRadius: 16,
        padding: 14,
        boxShadow: 'var(--os-elevation-1)',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        cursor: onClick ? 'pointer' : 'default',
        ...style,
      }}
    >
      {id && (
        <button
          onClick={(e) => { e.stopPropagation(); hide(id) }}
          title="Hide widget"
          style={{
            position: 'absolute', top: 8, right: 8,
            width: 18, height: 18, borderRadius: 6,
            background: hover ? 'var(--os-hover-strong)' : 'transparent',
            border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'var(--os-muted)', opacity: hover ? 1 : 0, zIndex: 2,
            transition: 'opacity 0.15s',
          }}
        >
          <X size={12} />
        </button>
      )}
      {children}
    </div>
  )
}

function WidgetTitle({ icon, label, color = '#818cf8' }: { icon: React.ReactNode; label: string; color?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
      <span style={{ color }}>{icon}</span>
      <span style={{ fontSize: 10, fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</span>
    </div>
  )
}

// ── 1. Clock Widget (Sol üst köşe - özel konum) ─────────────────────────────────
function ClockWidget({ fontSize = 42 }: { fontSize?: number }) {
  const [time, setTime] = useState(new Date())
  useEffect(() => { const t = setInterval(() => setTime(new Date()), 1000); return () => clearInterval(t) }, [])
  return (
    <div style={{ position: 'absolute', top: 20, left: 20, textAlign: 'center', zIndex: 10 }}>
      <div style={{
        fontSize,
        fontWeight: 200,
        color: 'var(--os-text-strong)',
        lineHeight: 1,
        textShadow: '0 2px 20px rgba(0,0,0,0.3)',
        letterSpacing: '-0.02em',
      }}>
        {time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
      </div>
      <div style={{ fontSize: 11, color: 'var(--os-muted)', marginTop: 6, fontWeight: 500 }}>
        {time.toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long' })}
      </div>
    </div>
  )
}

// ── 2. Tasks Widget (Sağ panel) ───────────────────────────────────────────────
const TasksWidget = memo(() => {
  const [tasks, setTasks] = useState<Task[]>([])
  const [loading, setLoading] = useState(true)

  const loadTasks = () => {
    apiFetch(`${API_URL}/api/tasks?status=todo&limit=5`)
      .then(r => r.json())
      .then((d: any) => { setTasks(d?.tasks ?? d?.data ?? []); setLoading(false) })
      .catch(() => setLoading(false))
  }

  useEffect(() => { loadTasks() }, [])

  // runai:task-alert → listeyi yenile
  useEffect(() => {
    const handler = () => loadTasks()
    window.addEventListener('runai:task-alert', handler)
    return () => window.removeEventListener('runai:task-alert', handler)
  }, [])

  return (
    <Widget id="tasks" onClick={() => window.dispatchEvent(new CustomEvent('runai:open-window', { detail: 'task-manager' }))}>
      <WidgetTitle icon={<CheckCircle2 size={12} />} label="Tasks" color="var(--os-success)" />
      {loading ? (
        <div style={{ fontSize: 11, color: 'var(--os-muted)' }}>Loading...</div>
      ) : tasks.length === 0 ? (
        <div>
          <div style={{ fontSize: 11, color: 'var(--os-muted)' }}>No tasks</div>
          <button onClick={(e) => { e.stopPropagation(); window.dispatchEvent(new CustomEvent('runai:open-window', { detail: 'task-manager' })) }} style={{ marginTop: 6, fontSize: 10, color: 'var(--os-accent)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>+ Create first task</button>
        </div>
      ) : (
        tasks.slice(0, 4).map((t: Task) => (
          <div key={t.id} style={{ fontSize: 12, marginBottom: 6, color: 'var(--os-text)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--os-success)' }} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.title}</span>
          </div>
        ))
      )}
    </Widget>
  )
})

// ── 3. Skills Widget (Sağ panel) ─────────────────────────────────────────────
const SkillsWidget = memo(() => {
  const [count, setCount] = useState<number | null>(null)
  const [lastResult, setLastResult] = useState<string | null>(null)

  useEffect(() => {
    apiFetch(`${API_URL}/api/aicloud/skills/installed`)
      .then(r => r.json())
      .then((d: any) => setCount((d?.skills ?? d?.data ?? []).length))
      .catch(() => setCount(0))
  }, [])

  // runai:skill-result → son sonucu göster
  useEffect(() => {
    const handler = (e: Event) => {
      const { skillName, status } = (e as CustomEvent).detail as { skillName: string; status: string }
      setLastResult(status === 'failed' ? `✗ ${skillName}` : `✓ ${skillName}`)
    }
    window.addEventListener('runai:skill-result', handler)
    return () => window.removeEventListener('runai:skill-result', handler)
  }, [])

  return (
    <Widget id="skills" onClick={() => window.dispatchEvent(new CustomEvent('runai:open-window', { detail: 'store' }))}>
      <WidgetTitle icon={<Zap size={12} />} label="Skills" color="var(--os-warning)" />
      {count === 0 ? (
        <div>
          <div style={{ fontSize: 11, color: 'var(--os-muted)' }}>No skills installed</div>
          <button onClick={(e) => { e.stopPropagation(); window.dispatchEvent(new CustomEvent('runai:open-window', { detail: 'store' })) }} style={{ marginTop: 6, fontSize: 10, color: 'var(--os-accent)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Browse Store</button>
        </div>
      ) : (
        <>
          <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--os-text-strong)' }}>{count === null ? '...' : count}</div>
          <div style={{ fontSize: 10, color: 'var(--os-muted)', marginTop: 2 }}>{lastResult ?? 'Installed Skills'}</div>
        </>
      )}
    </Widget>
  )
})

// ── 4. Agents Widget (Sağ panel) ─────────────────────────────────────────────
const AgentsWidget = memo(() => {
  const [agentCount, setAgentCount] = useState<number | null>(null)

  useEffect(() => {
    apiFetch(`${API_URL}/api/flzhub/agents?limit=4`)
      .then(r => r.json())
      .then((d: any) => setAgentCount((d?.agents ?? d?.data ?? []).length))
      .catch(() => setAgentCount(0))
  }, [])

  return (
    <Widget id="flzfamily" onClick={() => window.dispatchEvent(new CustomEvent('runai:open-window', { detail: 'flzhub' }))}>
      <WidgetTitle icon={<Bot size={12} />} label="Agents" color="#ec4899" />
      {agentCount === 0 ? (
        <div>
          <div style={{ fontSize: 11, color: 'var(--os-muted)' }}>No agents yet</div>
          <button onClick={(e) => { e.stopPropagation(); window.dispatchEvent(new CustomEvent('runai:open-window', { detail: 'flzhub' })) }} style={{ marginTop: 6, fontSize: 10, color: 'var(--os-accent)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Meet the family</button>
        </div>
      ) : (
        <>
          <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--os-text-strong)' }}>{agentCount === null ? '...' : agentCount}</div>
          <div style={{ fontSize: 10, color: 'var(--os-muted)', marginTop: 2 }}>Active Agents</div>
        </>
      )}
    </Widget>
  )
})

// ── 5. Workspace Snapshot Widget (Sağ panel) ────────────────────────────────
const WorkspaceSnapshotWidget = memo(() => {
  const [snapshots, setSnapshots] = useState<string[]>([])

  useEffect(() => {
    const saved = localStorage.getItem('workspace-snapshots')
    if (saved) setSnapshots(JSON.parse(saved))
  }, [])

  const handleSave = () => {
    const now = new Date()
    const name = `${now.toLocaleDateString()} ${now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
    window.dispatchEvent(new CustomEvent('runai:get-windows', { detail: { callback: (w: any) => localStorage.setItem(`snapshot-${name}`, JSON.stringify(w)) } }))
    const updated = [...snapshots, name]
    setSnapshots(updated)
    localStorage.setItem('workspace-snapshots', JSON.stringify(updated))
  }

  const handleLoad = (name: string) => {
    const saved = localStorage.getItem(`snapshot-${name}`)
    if (saved) {
      const windows = JSON.parse(saved)
      window.dispatchEvent(new CustomEvent('runai:restore-windows', { detail: windows }))
    }
  }

  return (
    <Widget id="workspace">
      <WidgetTitle icon={<Save size={12} />} label="Workspace" color="var(--os-info)" />
      <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
        <button
          onClick={handleSave}
          style={{ flex: 1, padding: '8px 10px', fontSize: 11, borderRadius: 8, background: 'var(--os-info-bg)', border: '1px solid var(--os-border-strong)', color: 'var(--os-info)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontWeight: 500 }}
        >
          <Save size={12} /> Save
        </button>
        <button
          onClick={() => snapshots.length > 0 && handleLoad(snapshots[snapshots.length - 1])}
          style={{ flex: 1, padding: '8px 10px', fontSize: 11, borderRadius: 8, background: 'var(--os-hover)', border: '1px solid var(--os-border)', color: 'var(--os-text)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontWeight: 500 }}
        >
          <FolderOpen size={12} /> Load
        </button>
      </div>
      <div style={{ fontSize: 10, color: 'var(--os-muted)' }}>
        {snapshots.length} snapshots
      </div>
    </Widget>
  )
})

// ── Main Component (macOS Sonoma sağ panel düzeni) ─────────────────────────
export default function DesktopHero() {
  const { isVisible } = useWidgetVisibility()
  const { isMobile, isTablet } = useResponsive()
  useLiveWallpaper()

  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null)
  const [isDragOver, setIsDragOver] = useState(false)

  const panelWidth = isMobile ? 0 : isTablet ? 160 : 200
  const clockFontSize = isMobile ? 32 : 42

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    // Files dragged from RunFiles (internal drag)
    const payload = localStorage.getItem('runai_drag_payload')
    if (payload) {
      try {
        const data = JSON.parse(payload) as { fileId: number; filename: string; streamUrl: string; mimeType: string; mediaType: string }
        // Open the file with the appropriate app
        window.dispatchEvent(new CustomEvent('runai:open-file', {
          detail: {
            windowId: data.mediaType === 'video' || data.mediaType === 'audio' || data.mediaType === 'image' ? 'run-player' : 'run-pad',
            fileId: data.fileId,
            filename: data.filename,
            streamUrl: data.streamUrl,
            mediaType: data.mediaType,
            mimeType: data.mimeType,
          }
        }))
        localStorage.removeItem('runai_drag_payload')
        return
      } catch { /* ignore */ }
    }
    // Files dragged from OS (external files) — Puter pattern: dataTransfer.items → upload
    const items = e.dataTransfer?.items
    if (items && items.length > 0) {
      const filesToUpload: File[] = []
      Array.from(items).forEach(item => {
        if (item.kind === 'file') {
          const file = item.getAsFile()
          if (file) filesToUpload.push(file)
        }
      })
      if (filesToUpload.length > 0) {
        // Open RunFiles and upload
        window.dispatchEvent(new CustomEvent('runai:open-window', { detail: 'run-files' }))
        window.dispatchEvent(new CustomEvent('runai:desktop-drop-files', { detail: { files: filesToUpload } }))
      }
    }
  }

  return (
    <div
      style={{
        position: 'absolute', inset: 0, overflow: 'hidden',
        outline: isDragOver ? '3px dashed var(--os-accent)' : 'none',
        outlineOffset: '-4px',
      }}
      onContextMenu={(e) => {
        e.preventDefault()
        e.nativeEvent.stopImmediatePropagation()
        // Global flag check — window already handled this
        if ((window as any).__runai_window_context_menu) return
        const target = e.target as HTMLElement
        if (
          target.closest('[data-window-id]') ||
          target.closest('[data-focused]') ||
          target.closest('[role="dialog"]')
        ) return
        setContextMenu({ x: e.clientX, y: e.clientY })
      }}
      onDragOver={(e) => { e.preventDefault(); setIsDragOver(true) }}
      onDragEnter={() => setIsDragOver(true)}
      onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsDragOver(false) }}
      onDrop={handleDrop}
    >
      {/* Sol üst: Büyük saat widget'ı */}
      {isVisible('clock') && <ClockWidget fontSize={clockFontSize} />}

      {/* Sağ panel: Widget'lar (macOS Sonoma tarzı) — mobile'da gizli */}
      {!isMobile && (
        <div style={{
          position: 'absolute',
          top: 16,
          right: 16,
          width: panelWidth,
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          zIndex: 10,
        }}>
          {isVisible('tasks') && <TasksWidget />}
          {isVisible('skills') && <SkillsWidget />}
          {isVisible('flzfamily') && <AgentsWidget />}
          {isVisible('workspace') && <WorkspaceSnapshotWidget />}
          {isVisible('metrics') && <MetricsWidget />}
          {isVisible('twin') && <TwinWidget />}
          {isVisible('pulse') && <PulseWidget />}
        </div>
      )}

      {/* WallpaperPicker removed — accessible via Settings */}

      {contextMenu && (
        <DesktopContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={() => setContextMenu(null)}
        />
      )}
    </div>
  )
}
